import React from "react";
import moment from "moment";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "../../../lib/utils";
import { User } from "../../../features/user";
import { ColCollaborator, ColComment } from "../types";
import { useCollaborationMessage } from "./hooks/useCollaborationMessage";

type CollaborationMessageProps = {
  comment: ColComment;
  previousComment?: ColComment;
  nextComment?: ColComment;
  collaborators: ColCollaborator[];
  currentUser: User;
  onDelete?: (comment: ColComment) => void;
  onEdit?: (comment: ColComment) => void;
  isEditing?: boolean;
  postState: string;
  disabled?: boolean;
};

function CollaborationMessage({
  comment,
  previousComment,
  nextComment,
  collaborators,
  currentUser,
  onDelete,
  onEdit,
  isEditing,
  postState,
  disabled,
}: CollaborationMessageProps) {
  const {
    displayName,
    formattedTime,
    isOwnMessage,
    isExternal,
    isUnread,
    showDateSeparator,
    canStackWithNext,
    showHeader,
    showAvatar,
    showTimeInGutter,
    gutterTime,
    headerLabel,
    headerBadge,
    actions,
    content,
    sender,
    isChangeRequest,
  } = useCollaborationMessage({
    comment,
    previousComment,
    nextComment,
    collaborators,
    currentUser,
    onDelete,
    onEdit,
    isEditing,
    postState,
    disabled,
  });

  if (!content) {
    return null;
  }

  return (
    <div className={cn(canStackWithNext ? "mb-[0px]" : "mb-[8px]")}>
      {showDateSeparator && comment.createdAt && (
        <DateSeparator label={moment(comment.createdAt).format("D MMM YYYY")} />
      )}

      <MessageWrapper
        displayName={displayName}
        label={headerLabel}
        formattedTime={formattedTime}
        showHeader={showHeader}
        showTimeInGutter={showTimeInGutter}
        gutterTime={gutterTime}
        isExternal={isExternal}
        showAvatar={showAvatar}
        avatarName={displayName}
        avatarImage={sender.profileImage}
        isOwnMessage={isOwnMessage}
        isEditing={isEditing}
        isUnread={isUnread}
        headerBadge={headerBadge}
        actions={actions}
        isChangeRequest={isChangeRequest}
      >
        {content}
      </MessageWrapper>
    </div>
  );
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="px-[16px]">
      <div className="mb-[8px] flex justify-center relative">
        <div className="absolute top-[50%] -translate-y-1/2 left-0 right-0 h-[1px] bg-neutral-100" />
        <span className="relative bg-white rounded-full border border-neutral-100 px-[8px] py-[4px] text-[10px] font-[400] text-neutral-500">
          {label}
        </span>
      </div>
    </div>
  );
}

interface MessageWrapperProps {
  displayName: string;
  label?: string;
  formattedTime: string;
  showHeader: boolean;
  showTimeInGutter: boolean;
  gutterTime?: string;
  isExternal: boolean;
  showAvatar: boolean;
  avatarName: string;
  avatarImage?: string;
  isOwnMessage: boolean;
  isEditing?: boolean;
  isUnread: boolean;
  headerBadge?: React.ReactNode;
  actions?: {
    onEdit?: () => void;
    onDelete?: () => void;
  };
  children: React.ReactNode;
  isChangeRequest?: boolean;
}

function MessageWrapper({
  displayName,
  label,
  formattedTime,
  showHeader,
  showTimeInGutter,
  gutterTime,
  isExternal,
  showAvatar,
  avatarName,
  avatarImage,
  isOwnMessage,
  isEditing,
  headerBadge,
  actions,
  children,
  isChangeRequest,
}: MessageWrapperProps) {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = React.useState(false);

  return (
    <div
      data-editing={isEditing ? true : undefined}
      className={cn(
        "group/message relative flex gap-[8px] font-['Inter'] px-[16px] py-[8px]",
        "transition-colors duration-150 ease-linear",
        "focus-within:bg-[#FCFAFF] hover:bg-[#FCFAFF]",
        isEditing && "bg-[#FCFAFF]"
      )}
    >
      {showAvatar ? (
        <MessageAvatar
          name={avatarName}
          image={avatarImage}
          isExternal={isExternal}
          isOwnMessage={isOwnMessage}
          isChangeRequest={isChangeRequest}
        />
      ) : (
        <></>
      )}

      <div className="flex flex-col gap-[4px] flex-1">
        {actions && (actions.onDelete || actions.onEdit) && (
          <div
            data-editing={isEditing ? true : undefined}
            data-menu-open={isActionsMenuOpen ? true : undefined}
            className={cn(
              "pointer-events-none absolute right-[8px] top-[8px] flex opacity-0 transition-opacity duration-150 data-[editing=true]:pointer-events-auto data-[editing=true]:opacity-100 group-hover/message:pointer-events-auto group-hover/message:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100",
              isActionsMenuOpen && "pointer-events-auto opacity-100"
            )}
          >
            <DropdownMenu
              open={isActionsMenuOpen}
              onOpenChange={setIsActionsMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Open message actions"
                  className="flex h-[24px] w-[24px] p-[4px] items-center justify-center rounded-[8px] border border-transparent bg-white text-[#1f1f1f] shadow-sm transition-colors hover:bg-[#F2EEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F31FF] focus-visible:ring-offset-2"
                >
                  <i className="fa-solid fa-ellipsis-vertical text-[14px]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="min-w-[120px] rounded-[12px] border border-neutral-100 bg-white shadow-lg"
              >
                {actions.onEdit && (
                  <DropdownMenuItem
                    onSelect={() => actions.onEdit?.()}
                    className="rounded-[8px] px-[12px] py-[8px] text-[14px] text-[#333] font-[400] cursor-pointer"
                  >
                    <i className="fa-regular fa-pen-to-square text-[14px] text-[#333]" />
                    Edit
                  </DropdownMenuItem>
                )}
                {actions.onDelete && (
                  <DropdownMenuItem
                    onSelect={() => actions.onDelete?.()}
                    variant="destructive"
                    className="rounded-[8px] px-[12px] py-[8px] text-[14px] text-[#C83F2D] font-[400] cursor-pointer"
                  >
                    <i className="fa-regular fa-trash text-[14px] text-[#C83F2D]" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {showHeader && (
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-[4px] text-sm leading-[16px] text-gray-900">
              <span className="font-[600] text-[12px]">{displayName}</span>
              {label && (
                <span className="text-[10px] text-gray-500">({label})</span>
              )}
              {formattedTime && (
                <span className="text-[10px] text-gray-500">
                  {formattedTime}
                </span>
              )}
            </div>
            {headerBadge}
          </div>
        )}

        <div className={cn("flex gap-[8px]")}>
          {showTimeInGutter && (gutterTime || formattedTime) && (
            <span className="flex w-[32px] shrink-0 items-center justify-end text-[10px] leading-[12px] text-gray-500">
              {gutterTime || formattedTime}
            </span>
          )}
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

interface MessageAvatarProps {
  name: string;
  image?: string;
  isExternal: boolean;
  isOwnMessage: boolean;
  isChangeRequest?: boolean;
}

function MessageAvatar({
  name,
  image,
  isExternal,
  isOwnMessage,
  isChangeRequest = false,
}: MessageAvatarProps) {
  const initials = getInitials(name);
  return (
    <div
      className={cn(
        "relative flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full text-[10px] font-[400] uppercase",
        isOwnMessage ? "bg-[#6A5BFF] text-white" : "bg-gray-200 text-gray-700"
      )}
    >
      {image ? (
        <img src={image} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span>{initials || "?"}</span>
      )}
      {isExternal && (
        <span className="absolute -bottom-[1px] -right-[1px] flex h-[10px] w-[10px] items-center justify-center border border-white bg-[#F59E0B] text-white">
          <i
            className={`fa-solid ${isChangeRequest ? "fa-arrow-left" : "fa-arrow-right"} text-[7px]`}
          />
        </span>
      )}
    </div>
  );
}

function getInitials(name: string) {
  if (!name) {
    return "";
  }
  const parts = name.trim().split(/\s+/);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "");
  return initials.join("");
}

export default CollaborationMessage;
